/**
 * The guide's drawn phone screens (split out of guide-manual.ts, 2026-10-14).
 *
 * Two kinds of screen. Ours, the dashboard card and the QR modal, are the
 * dashboard's own markup; the phone's (iOS Settings, Samsung's SIM manager)
 * are drawn after the real ones, row for row, in the reader's language. Both
 * the server and the client render them through renderGuidePhone().
 */
import { icon } from './ui-icons.js';
export function guideEsc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
const TAP = '<span class="gs-tap" aria-hidden="true"></span>';
// ─── Phone screens ─────────────────────────────────────────────────────────
//
// Two kinds of screen, drawn two ways (2026-09-30).
//
// Ours — the dashboard card and the QR modal — are the dashboard's own markup
// and classes, laid out at a 375px phone width and zoomed into the frame. Their
// styles are the dashboard's, copied and scoped by
// scripts/build-guide-screens-css.mjs, so a dashboard restyle reaches the guide
// (a test fails until the copy is regenerated).
//
// The phone's — iOS Settings, Samsung's SIM manager — follow the real screens
// row for row, in the reader's language, with the carrier name the phone
// actually shows: Roamzy.
/** A sample of our eSIM ID format (the MSISDN, 10 digits). */
const SAMPLE_ESIM_ID = '2040518377';
/** A personal activation code, shown masked the way a screenshot would redact it. */
const SAMPLE_LPA = 'LPA:1$••••••••••$••••-••••-••••';
const SAMPLE_ADDRESS = '••••••••••';
const SAMPLE_CODE = '••••-••••-••••';
/**
 * A QR-shaped pattern (21×21 modules, three finder squares, fixed noise). It
 * encodes nothing: the real code is personal, and a scannable sample would
 * send a reader's camera somewhere.
 */
function qrSvg() {
    const n = 21;
    const on = (x, y) => {
        const finder = (fx, fy) => {
            const dx = x - fx, dy = y - fy;
            if (dx < 0 || dy < 0 || dx > 6 || dy > 6)
                return null;
            return dx === 0 || dy === 0 || dx === 6 || dy === 6 || (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4);
        };
        for (const [fx, fy] of [[0, 0], [14, 0], [0, 14]]) {
            const f = finder(fx, fy);
            if (f !== null)
                return f;
        }
        if ((x === 7 || y === 7) && (x < 8 && y < 8))
            return false;
        if ((x === 13 && y < 8) || (y === 7 && x > 12) || (x === 7 && y > 12) || (y === 13 && x < 8))
            return false;
        return ((x * 7 + y * 13 + x * y * 3) % 5) < 2;
    };
    let d = '';
    for (let y = 0; y < n; y++)
        for (let x = 0; x < n; x++)
            if (on(x, y))
                d += `M${x} ${y}h1v1h-1z`;
    return `<svg class="gs-qrcode" viewBox="0 0 ${n} ${n}" aria-hidden="true"><path d="${d}"/></svg>`;
}
const QR = qrSvg();
/** "$0.50" — the dashboard's money style (frontend/src/utils/format.ts fmtMoney). */
function money(micro) {
    return `$${(Math.abs(micro) / 1_000_000).toFixed(2)}`;
}
function btn(cls, label, tap) {
    return `<button type="button" class="${cls}${tap ? ' gs-tap-host' : ''}">${guideEsc(label)}${tap ? TAP : ''}</button>`;
}
/** The sample account on the drawn screens. */
const SAMPLE_NAME = 'Alex';
/**
 * The eSIM tab as the installed app (PWA) shows it on a phone: the site
 * header, the dashboard header and tabs (routes/dashboard/_shared.ts
 * shellHtml), the card (esims.ts activeCardHtml). It is scrolled so the
 * card's buttons are in view, under the header, which stays put as it does
 * in the app. Headings and landmarks are plain elements here: this is a
 * picture inside the guide, and the guide keeps a single h1.
 */
function dashScreen(ui, credit, locale) {
    // The strip scrolls sideways; a phone shows the first four, the fourth cut
    // at the edge. The ones past the edge are not drawn.
    const tabs = [ui.tabEsims, ui.tabAnalytics, ui.tabHistory, ui.tabReferral]
        .map((label, i) => `<span class="dash-tab${i === 0 ? ' is-active' : ''}">${guideEsc(label)}</span>`).join('');
    return `<div class="gscreen-app gscreen-app--pwa" inert>
    <div class="nav"><div class="nav__inner">
      <span class="nav__logo" lang="en">Roamzy<sup>.io</sup></span>
      <div class="nav__actions">
        <button type="button" class="nav__lang"><span class="nav__lang-globe" aria-hidden="true">${icon('globe', 14)}</span><span class="nav__lang-code">${guideEsc(locale.toUpperCase())}</span></button>
        <div class="nav__user-menu"><button type="button" class="nav__user"><span class="nav__user-avatar">${SAMPLE_NAME[0]}</span><span class="nav__user-name">${SAMPLE_NAME}</span><span class="nav__user-chevron" aria-hidden="true">▾</span></button></div>
        <button type="button" class="nav__burger"><span></span><span></span><span></span></button>
      </div>
    </div></div>
    <div class="gscreen-app__scroll"><div class="gscreen-app__page">
      <div class="dash-route"><div class="container">
        <div class="dash-header">
          <div>
            <div class="section__kicker gscreen-app__kicker">${guideEsc(ui.kicker)}</div>
            <div class="dash-title">${guideEsc(ui.greeting)}, ${SAMPLE_NAME}.</div>
          </div>
          <div class="dash-header__cta"><button type="button" class="btn btn--accent">${guideEsc(ui.topupCta)}</button></div>
        </div>
        <div class="dash-tabs">${tabs}</div>
        <div class="dash-panel"><div class="dash-panel-inner"><div class="dash-esim-grid">
          <article class="dash-esim-card dash-esim-card--active">
            <header class="dash-esim-card__head">
              <span class="dash-esim-card__flag" aria-hidden="true">${icon('globe', 30)}</span>
              <div class="dash-esim-card__title">
                <div class="dash-esim-card__country">${guideEsc(ui.global)} <button type="button" class="dash-icon-btn">${icon('pencil', 14)}</button></div>
                <p class="dash-esim-card__rate">${guideEsc(ui.globalSub)}</p>
              </div>
              <span class="dash-sim-status dash-sim-status--awaiting dash-esim-card__status-pill"><span class="dash-sim-status__dot" aria-hidden="true"></span>${guideEsc(ui.awaiting)}</span>
            </header>
            <div class="dash-esim-card__hero">
              <div class="dash-esim-card__balance"><span class="label">${guideEsc(ui.balance)}</span><span class="dash-esim-card__balance-amount mono">${credit === undefined ? '—' : money(credit)}</span></div>
              <div class="dash-esim-card__id"><span class="label">${guideEsc(ui.idLabel)}</span><span class="dash-esim-card__id-value mono">${SAMPLE_ESIM_ID} <button type="button" class="dash-icon-btn">${icon('copy', 14)}</button></span></div>
            </div>
            <div class="dash-esim-card__body dash-esim-card__body--two-col">
              <div class="dash-esim-card__metric"><span class="label">${guideEsc(ui.consumed)}</span><span class="mono">0 MB</span></div>
              <div class="dash-esim-card__metric"><span class="label">${guideEsc(ui.spent)}</span><span class="mono">${money(0)}</span></div>
            </div>
            <details class="dash-esim-card__breakdown"><summary>${guideEsc(ui.breakdown)}</summary></details>
            <footer class="dash-esim-card__actions">
              ${btn('btn btn--accent btn--sm', ui.topup, false)}
              ${btn('btn btn--ghost btn--sm', ui.showQr, true)}
            </footer>
          </article>
        </div></div></div>
      </div></div>
    </div></div>
  </div>`;
}
/** The QR modal (frontend/src/modals/qr.ts) as the reader's phone lays it
 *  out: on an iPhone "Tap to install" comes first and is the primary card;
 *  elsewhere the QR does. "tap" shows the top of the modal with the button
 *  ringed; "scan" the modal as a computer shows it, QR first, the QR ringed;
 *  "copy" the modal scrolled to the end, where the manual code and its Copy
 *  button are. */
function dashQr(ui, device, focus) {
    const tapFirst = device === 'ios' && focus !== 'scan';
    const head = (ico, title) => `<div class="qr-modal__method-head"><span class="qr-modal__method-icon" aria-hidden="true">${icon(ico, 18)}</span><div class="qr-modal__method-title">${guideEsc(title)}</div></div>`;
    const ring = focus === 'scan';
    const qr = `<div class="qr-modal__method qr-modal__method--qr" data-primary="${!tapFirst}">
      ${head('scan', ui.scanTitle)}
      <div class="qr-modal__qr${ring ? ' gs-tap-host' : ''}">${QR}${ring ? TAP : ''}</div>
      ${btn('btn btn--ghost btn--sm', ui.saveQr, false)}
    </div>`;
    const tap = `<div class="qr-modal__method qr-modal__method--tap" data-primary="${tapFirst}">
      ${head('phone', ui.tapTitle)}
      <p class="qr-modal__method-desc">${guideEsc(ui.tapDesc)}</p>
      ${btn('btn btn--accent btn--full', ui.tapInstall, focus === 'tap')}
    </div>`;
    const manual = `<div class="qr-modal__method qr-modal__method--manual">
      ${head('keyboard', ui.manualTitle)}
      <p class="qr-modal__method-desc">${guideEsc(ui.manualDesc)}</p>
      <code class="qr-modal__lpa">${guideEsc(SAMPLE_LPA)}</code>
      ${btn('btn btn--ghost btn--sm', ui.copy, focus === 'copy')}
    </div>`;
    return `<div class="gscreen-app gscreen-app--modal${focus === 'copy' ? ' gscreen-app--end' : ''}" inert>
    <div class="modal-panel qr-modal">
      <button type="button" class="modal-close">×</button>
      <div class="qr-modal__title">${guideEsc(ui.ready)}</div>
      <p class="qr-modal__sub">${icon('globe', 15)} ${guideEsc(ui.global)}</p>
      <div class="qr-modal__methods">${tapFirst ? tap + qr : qr + tap}${manual}</div>
      <div class="qr-modal__actions"><span class="btn btn--ghost btn--sm">${guideEsc(ui.toDashboard)}</span><span class="qr-modal__more-link">${guideEsc(ui.fullGuides)}</span></div>
    </div>
  </div>`;
}
/** The small pictogram iOS and One UI put above a setup step. */
function glyph(device) {
    return device === 'ios'
        // iOS: the cellular antenna with its waves.
        ? '<svg class="gs-glyph" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 11v9M9.5 20h5"/><circle cx="12" cy="9.5" r="1.5"/><path d="M8.5 13a5 5 0 010-7M15.5 6a5 5 0 010 7M6 15.5a8.5 8.5 0 010-12M18 3.5a8.5 8.5 0 010 12"/></svg>'
        // One UI: a SIM card with a download arrow.
        : '<svg class="gs-glyph" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3h7l4 4v14H7z"/><path d="M12.5 9v6M10 12.5l2.5 2.5 2.5-2.5"/></svg>';
}
/** Settings app icons (Lucide shapes, ISC), white on the tile's colour. */
const ROW_ICON = {
    plane: '<path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>',
    wifi: '<path d="M12 20h.01"/><path d="M2 8.82a15 15 0 0 1 20 0"/><path d="M5 12.86a10 10 0 0 1 14 0"/><path d="M8.5 16.43a5 5 0 0 1 7 0"/>',
    bt: '<path d="m7 7 10 10-5 5V2l5 5L7 17"/>',
    cell: '<circle cx="12" cy="9.5" r="1.7"/><path d="M12 11.5V21"/><path d="M8.2 5.7a5.4 5.4 0 0 0 0 7.6M15.8 5.7a5.4 5.4 0 0 1 0 7.6M5.3 2.8a9.5 9.5 0 0 0 0 13.4M18.7 2.8a9.5 9.5 0 0 1 0 13.4"/>',
    link: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
    battery: '<rect width="16" height="10" x="2" y="7" rx="2.5"/><path d="M22 11v2"/><path d="M6 10v4M9 10v4"/>',
    gear: '<circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1 7 17M17 7l2.1-2.1"/>',
    sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>',
    bell: '<path d="M10.27 21a2 2 0 0 0 3.46 0"/><path d="M3.26 15.33A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.67C19.41 13.96 18 12.5 18 8A6 6 0 0 0 6 8c0 4.5-1.41 5.96-2.74 7.33"/>',
    devices: '<path d="M18 8V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h8"/><path d="M10 19v-3.96"/><path d="M7 19h5"/><rect width="6" height="10" x="16" y="12" rx="2"/>',
    modes: '<circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>',
    sound: '<path d="M11 4.7a.7.7 0 0 0-1.2-.5L6.4 7.6A1.4 1.4 0 0 1 5.4 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.4a1.4 1.4 0 0 1 1 .4l3.4 3.4a.7.7 0 0 0 1.2-.5z"/><path d="M16 9a5 5 0 0 1 0 6"/>',
};
function rowIcon(name) {
    return `<span class="gs-ico gs-ico--${name}" aria-hidden="true"><svg viewBox="0 0 24 24">${ROW_ICON[name]}</svg></span>`;
}
function rowHtml(r) {
    if (r.head)
        return `<div class="gs-cap">${guideEsc(r.t)}</div>`;
    if (r.note)
        return `<div class="gs-note">${guideEsc(r.t)}</div>`;
    const toggle = r.toggle || r.flip;
    const control = toggle
        ? `<span class="gs-toggle${r.flip ? ' gs-toggle--flip' : r.off ? '' : ' gs-toggle--on'}"></span>`
        : r.check ? '<span class="gs-check">✓</span>'
            : r.chev ? '<span class="gs-chev">›</span>' : '';
    // The tap marker sits on the control the reader presses, not on the text.
    const end = control || r.tap ? `<span class="gs-end">${control}${r.tap ? TAP : ''}</span>` : '';
    const lead = r.icon ? rowIcon(r.icon) : r.badge ? `<span class="gs-badge" aria-hidden="true">${guideEsc(r.badge)}</span>` : '';
    const main = `<span class="gs-row__main"><span class="gs-row__t">${guideEsc(r.t)}</span>${r.sub ? `<span class="gs-row__sub">${guideEsc(r.sub)}</span>` : ''}</span>`;
    const value = r.value ? `<span class="gs-row__value">${guideEsc(r.value)}</span>` : '';
    const cls = `gs-row${r.tap ? ' gs-row--tap' : ''}${r.link ? ' gs-row--link' : ''}${r.icon ? ' gs-row--icon' : ''}`;
    // The value and the control travel together: on a narrow phone they drop
    // under a long label instead of cutting it.
    const aside = value || end ? `<span class="gs-row__aside">${value}${end}</span>` : '';
    return `<div class="${cls}">${lead}${main}${aside}</div>`;
}
/** Rows into groups: a caption row starts a new group, as Settings lays them out. */
function groups(rows) {
    const out = [];
    let cur = [];
    const flush = () => { if (cur.length)
        out.push(`<div class="gs-group">${cur.join('')}</div>`); cur = []; };
    for (const r of rows) {
        if (r.head || r.note) {
            flush();
            out.push(rowHtml(r));
        }
        else
            cur.push(rowHtml(r));
    }
    flush();
    return out.join('');
}
function listScreen(s, device) {
    // The root page: its name large on the left, as both systems draw it.
    // iOS: "‹ Settings" on the left, the page title centred. One UI: a back
    // arrow and the page's own title, large, on the left.
    const nav = s.large
        ? `<div class="gs-nav gs-nav--large"><span class="gs-title gs-title--large">${guideEsc(s.title)}</span></div>`
        : device === 'ios'
            ? `<div class="gs-nav">${s.back ? `<span class="gs-back">‹ ${guideEsc(s.back)}</span>` : ''}<span class="gs-nav__title">${guideEsc(s.title)}</span></div>`
            : `<div class="gs-nav">${s.back ? '<span class="gs-back" aria-hidden="true">‹</span>' : ''}<span class="gs-title">${guideEsc(s.title)}</span></div>`;
    return `${nav}<div class="gs-list">${groups(s.rows)}</div>`;
}
function screenBody(s, device, ui, o) {
    switch (s.kind) {
        case 'card':
            return dashScreen(ui, o.trialCreditMicro, o.locale ?? 'en');
        case 'qr':
            return dashQr(ui, device, s.focus);
        case 'sheet':
            // One UI asks on a page of its own: the question centred, the button
            // bottom right, the way out ("Use a different network") bottom left.
            if (device === 'android') {
                return `<div class="gs-setup gs-setup--page">
          ${glyph(device)}
          <div class="gs-setup__title">${guideEsc(s.title)}</div>
          <div class="gs-setup__text">${guideEsc(s.text)}</div>
          <div class="gs-setup__foot">${s.cancel ? `<span class="gs-link">${guideEsc(s.cancel)}</span>` : '<span></span>'}<span class="gs-btn gs-btn--primary">${guideEsc(s.primary)}${TAP}</span></div>
        </div>`;
            }
            return `<div class="gs-dim"></div>
        <div class="gs-sheet">
          ${s.cancel ? `<div class="gs-sheet__cancel">${guideEsc(s.cancel)}</div>` : ''}
          ${glyph(device)}
          <div class="gs-sheet__title">${guideEsc(s.title)}</div>
          <div class="gs-sheet__text">${guideEsc(s.text)}</div>
          <div class="gs-btn gs-btn--primary">${guideEsc(s.primary)}${TAP}</div>
        </div>`;
        case 'options': {
            // With no row to tap, the step is "keep the choice, press the button":
            // the ring goes on the button.
            const buttonTap = !s.rows.some((r) => r.tap);
            return `${device === 'ios' ? '<div class="gs-dim"></div>' : ''}
        <div class="gs-setup">
          ${s.cancel ? `<div class="gs-sheet__cancel">${guideEsc(s.cancel)}</div>` : ''}
          ${glyph(device)}
          <div class="gs-setup__title">${guideEsc(s.title)}</div>
          ${s.text ? `<div class="gs-setup__text">${guideEsc(s.text)}</div>` : ''}
          <div class="gs-list">${groups(s.rows)}</div>
          ${s.primary ? `<div class="gs-btn gs-btn--primary">${guideEsc(s.primary)}${buttonTap ? TAP : ''}</div>` : ''}
        </div>`;
        }
        case 'form':
            return `<div class="gs-setup gs-setup--form">
          ${glyph(device)}
          <div class="gs-setup__title">${guideEsc(s.title)}</div>
          <div class="gs-setup__text">${guideEsc(s.text)}</div>
          <div class="gs-field"><span class="gs-field__value">${guideEsc(s.value)}</span></div>
          <div class="gs-setup__foot">${s.secondary ? `<span class="gs-link">${guideEsc(s.secondary)}</span>` : '<span></span>'}<span class="gs-btn gs-btn--primary">${guideEsc(s.primary)}${TAP}</span></div>
        </div>`;
        case 'fields':
            // iOS fills both fields from the one pasted string; the third stays empty.
            return `<div class="gs-dim"></div>
        <div class="gs-setup gs-setup--fields">
          <div class="gs-setup__top"><span class="gs-back" aria-hidden="true">‹</span><span class="gs-link gs-tap-host">${guideEsc(s.next)}${TAP}</span></div>
          ${glyph(device)}
          <div class="gs-setup__title">${guideEsc(s.title)}</div>
          <div class="gs-setup__text">${guideEsc(s.text)}</div>
          <div class="gs-list">
            <div class="gs-group"><div class="gs-row"><span class="gs-row__main"><span class="gs-row__t gs-mono">${SAMPLE_ADDRESS}</span></span></div><div class="gs-row"><span class="gs-row__main"><span class="gs-row__t gs-mono">${SAMPLE_CODE}</span></span></div></div>
            <div class="gs-group"><div class="gs-row"><span class="gs-row__main"><span class="gs-row__t gs-placeholder">${guideEsc(s.empty)}</span></span></div></div>
          </div>
        </div>`;
        case 'scan':
            return `<div class="gs-scan">
          ${s.title ? `<div class="gs-scan__title">${guideEsc(s.title)}</div>` : ''}
          ${s.hint ? `<div class="gs-scan__hint">${guideEsc(s.hint)}</div>` : ''}
          <div class="gs-viewfinder${s.monitor ? ' gs-viewfinder--monitor' : ''}">${s.monitor ? `<span class="gs-monitor"><span class="gs-viewfinder__qr">${QR}</span></span>` : ''}<span class="gs-frame" aria-hidden="true"></span></div>
          ${s.link ? `<div class="gs-link gs-scan__link${s.tapLink ? ' gs-tap-host' : ''}">${guideEsc(s.link)}${s.tapLink ? TAP : ''}</div>` : ''}
        </div>`;
        case 'list':
            return listScreen(s, device);
    }
}
function screenTitle(s, ui) {
    switch (s.kind) {
        case 'card': return ui.showQr;
        case 'qr': return s.focus === 'tap' ? ui.tapInstall : s.focus === 'scan' ? ui.scanTitle : ui.manualTitle;
        // One UI's camera has no title of its own: the step's link names it.
        case 'scan': return s.title ?? s.link ?? '';
        default: return s.title;
    }
}
/** A drawn phone showing one screen. Exported for tests. */
export function renderGuidePhone(s, device, c, o = {}) {
    const app = s.kind === 'card' || s.kind === 'qr';
    return `<figure class="manual-screen" role="img" data-md="skip" aria-label="${guideEsc(`${c.screen}: ${screenTitle(s, c.ui)}`)}">
    <div class="gphone gphone--${device}">
      <div class="gphone__screen gphone__screen--${s.kind}${app ? ' gphone__screen--app' : ''}">
        <div class="gphone__bar"><span>9:41</span><span class="gphone__icons"></span></div>
        <div class="gphone__content">${screenBody(s, device, c.ui, o)}</div>
      </div>
    </div>
  </figure>`;
}
